import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Seo } from '../components/Seo';
import { thumb, thumbSet } from '../lib/img';
import { parseDate } from '../lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://dyssc.com';

export const CAT_COLOR = {
  Rules: 'text-[#E02128] border-[#E02128]/40 bg-[#E02128]/10',
  Equipment: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
  Technique: 'text-blue-400 border-blue-500/40 bg-blue-500/10',
  News: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
};

export function formatDate(iso) {
  try {
    return parseDate(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  } catch { return ''; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState('All');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (cat) => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/blog${cat && cat !== 'All' ? `?category=${encodeURIComponent(cat)}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      setPosts(data.posts || []);
      setCategories(data.categories || []);
    } catch (e) { console.error('blog load failed', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(active); }, [active, load]);

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Blog', name: 'DYSSC Pickleball Journal', url: `${SITE}/blog`,
    blogPost: posts.slice(0, 15).map((p) => ({ '@type': 'BlogPosting', headline: p.title, url: `${SITE}/blog/${p.slug}`, datePublished: p.published_at })),
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white bg-texture" data-testid="blog-page">
      <Seo
        title="Pickleball Blog — Rules, Technique, Gear & News | DYSSC"
        description="The DYSSC Pickleball Journal: rules explained, technique drills, paddle buying guides and the latest on pickleball in India and the pro tours."
        canonical={`${SITE}/blog`}
        jsonLd={jsonLd}
      />

      <div className="max-w-6xl mx-auto px-6 pt-24 pb-12">
        <p className="text-[#E02128] font-heading tracking-[0.2em] text-xs uppercase mb-3">DYSSC Journal</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">The Pickleball Journal</h1>
        <p className="text-zinc-400 text-base md:text-lg max-w-2xl">
          Rules, technique, gear guides and the latest from the world of pickleball — written to help you play better and pick the right paddle.
        </p>

        {/* category filter */}
        <div className="flex flex-wrap gap-2 mt-8" data-testid="blog-categories">
          {['All', ...categories].map((c) => (
            <button key={c} onClick={() => setActive(c)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${active === c ? 'bg-white text-black border-white' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}
              data-testid={`blog-cat-${c}`}>
              {c}
            </button>
          ))}
        </div>

        {/* grid */}
        {loading ? (
          <div className="text-zinc-500 mt-16">Loading…</div>
        ) : (
          <>
            {/* featured — newest article (All view only) */}
            {active === 'All' && posts.length > 0 && (
              <Link to={`/blog/${posts[0].slug}`} className="group block mt-10" data-testid="blog-featured">
                <div className="grid lg:grid-cols-12 overflow-hidden rounded-2xl border border-zinc-800 bg-[#111214] group-hover:border-zinc-600 transition-colors">
                  <div className="lg:col-span-3 p-5 lg:p-6 flex items-center justify-center">
                    <img src={thumb(posts[0].cover, 480)} srcSet={thumbSet(posts[0].cover, [360, 480, 720])} sizes="260px" alt={posts[0].title}
                      width="1080" height="1440" fetchPriority="high" decoding="async"
                      className="w-full max-w-[260px] h-auto rounded-xl group-hover:scale-[1.02] transition-transform duration-500" />
                  </div>
                  <div className="lg:col-span-9 p-6 lg:p-10 flex flex-col justify-center">
                    <p className="text-[11px] font-heading uppercase tracking-[0.25em] text-[#E02128] mb-4">Latest article</p>
                    <h2 className="sr-only">{posts[0].title}</h2>
                    <p className="text-zinc-300 text-base lg:text-lg leading-relaxed line-clamp-4">{posts[0].excerpt}</p>
                    <div className="flex items-center gap-4 mt-5 text-xs text-zinc-500">
                      <span>{formatDate(posts[0].published_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {posts[0].read_time} min read</span>
                    </div>
                    <span className="inline-flex w-fit items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-[#E02128] text-white text-sm font-semibold group-hover:bg-[#c41d23] transition-colors" data-testid="featured-read-cta">
                      Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            <div className="columns-1 sm:columns-2 lg:columns-3 gap-7 mt-10" data-testid="blog-grid">
              {(active === 'All' ? posts.slice(1) : posts).map((p, i) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block break-inside-avoid mb-7" data-testid={`blog-card-${p.slug}`}>
                  <div className="overflow-hidden rounded-xl border border-zinc-800 bg-[#111214] group-hover:border-zinc-600 transition-colors">
                    <div className="overflow-hidden">
                      <img src={thumb(p.cover, 480)} srcSet={thumbSet(p.cover, [360, 480, 720])}
                        sizes="(min-width:1024px) 400px, (min-width:640px) 45vw, 92vw"
                        alt={p.title} width="1080" height="1440" decoding="async"
                        loading={i < 6 ? 'eager' : 'lazy'}
                        className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <h2 className="sr-only">{p.title}</h2>
                      <p className="text-sm text-zinc-400 line-clamp-2">{p.excerpt}</p>
                      <div className="flex items-center gap-3 mt-4 text-xs text-zinc-500">
                        <span>{formatDate(p.published_at)}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_time} min</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-auto group-hover:translate-x-1 transition-transform text-[#E02128]" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
